import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import type {
  CreateAutoJoinRuleInput,
  UpdateAutoJoinRuleInput,
} from '../types/spaces';

const KEY = (spaceId: string) => ['space', spaceId, 'auto-join-rules'] as const;

/** List a space's auto-join rules. Admin+ on the backend (403s otherwise). */
export function useAutoJoinRules(spaceId: string | undefined) {
  return useQuery({
    queryKey: KEY(spaceId ?? ''),
    queryFn: () => spaceApi(spaceId!).autoJoinRules.list(),
    enabled: !!spaceId,
  });
}

export function useCreateAutoJoinRule(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAutoJoinRuleInput) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).autoJoinRules.create(input);
    },
    onSuccess: () => {
      if (spaceId) queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
    },
  });
}

export function useUpdateAutoJoinRule(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ruleId,
      input,
    }: {
      ruleId: string;
      input: UpdateAutoJoinRuleInput;
    }) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).autoJoinRules.update(ruleId, input);
    },
    onSuccess: () => {
      if (spaceId) queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
    },
  });
}

export function useDeleteAutoJoinRule(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).autoJoinRules.remove(ruleId);
    },
    onSuccess: () => {
      if (spaceId) queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
    },
  });
}

export function useApplyAutoJoinRules(spaceId: string | undefined) {
  return useMutation({
    mutationFn: () => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).autoJoinRules.apply();
    },
  });
}

/** Targeted single-user reconcile. Invalidates the member roster on success. */
export function useReconcileUserAutoJoin(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).autoJoinRules.reconcileUser(userId);
    },
    onSuccess: () => {
      if (spaceId) {
        queryClient.invalidateQueries({
          queryKey: ['space', spaceId, 'members'],
        });
      }
    },
  });
}
